/**
 * StreakSheet — hoja "Tu racha": hero (racha actual + mejor marca), estado de
 * hoy, calendario navegable (heatmap por intensidad) y detalle por día.
 *
 * `variant="sheet"` = bottom-sheet (móvil); `variant="modal"` = centrado
 * (desktop). Todo lo agregado viene del backend: la racha de
 * `/progress/totals` y el calendario de `/progress/daily` (vía `useHomeStats`,
 * ya armado en un {@link StreakCalendar}). El DESGLOSE de un día — qué
 * actividades hiciste — se carga lazy al abrirlo, así la Home no arrastra el
 * historial entero por un detalle que quizá nadie abra.
 *
 * Sin costos en USD a propósito: ésta es la superficie de motivación — la
 * plata vive en el wallet y el SessionCostSheet.
 */
import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { listSessionsForDay, type SessionActivityType } from '../../../lib/sessionsApi.js';
import { ShortUnderline } from '../../../components/doodles/Doodles.js';
import {
  IconCap,
  IconChat,
  IconCheck,
  IconChevronLeft,
  IconChevronRight,
  IconClose,
  IconLayers,
  IconPlus,
  IconSpark,
  IconTarget,
} from '../icons.js';
import { StreakFlame } from './StreakFlame.js';
import {
  buildDayBreakdown,
  dayKeyOf,
  streakTier,
  type DayBreakdown,
  type StreakCalendar,
  type StreakDay,
} from './streakData.js';
import { useSheetDrag } from '../../../hooks/useSheetDrag.js';
import type { Routine } from '../../../lib/englishApi.js';
import { formatRoutine, isRestDay, weekdayOf } from '../../../lib/routine.js';
import '../../../components/routine/routine.css';

const MONTHS = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];
const DOW = ['L', 'M', 'X', 'J', 'V', 'S', 'D'];
const DOW_FULL = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];

function mondayIdx(d: Date): number {
  return (d.getDay() + 6) % 7;
}
function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}
function sameYmd(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function typeIcon(type: SessionActivityType): ReactNode {
  if (type === 'conversation') return <IconChat size={17} />;
  if (type === 'lesson') return <IconCap size={17} />;
  if (type === 'drill') return <IconTarget size={17} />;
  return <IconLayers size={17} />;
}

interface SelectedDay {
  y: number;
  m: number;
  d: number;
  rec: StreakDay;
}

// ─── Calendario de un mes ────────────────────────────────────────────────────

interface MonthCalendarProps {
  cursor: { y: number; m: number };
  byKey: Record<string, StreakDay>;
  today: Date;
  onPickDay: (day: SelectedDay) => void;
  /** Días de la rutina (lunes = 0); los demás se pintan libres. `null` = sin rutina. */
  routineWeekdays: readonly number[] | null;
  /** Desde cuándo rige la rutina: antes no hay días «libres» que pintar. */
  routineSince: Date | null;
}

function MonthCalendar({ cursor, byKey, today, onPickDay, routineWeekdays, routineSince }: MonthCalendarProps) {
  const { y, m } = cursor;
  const first = new Date(y, m, 1);
  const offset = mondayIdx(first);
  const dim = new Date(y, m + 1, 0).getDate();
  const cells: ReactNode[] = [];
  for (let i = 0; i < offset; i++) cells.push(<div key={`b${i}`} className="sk-cell blank" />);
  for (let d = 1; d <= dim; d++) {
    const dt = new Date(y, m, d);
    const isFuture = dt.getTime() > today.getTime();
    const isToday = sameYmd(dt, today);
    const rec = byKey[dayKeyOf(y, m, d)];
    const tier = rec ? streakTier(rec.minutes) : 0;
    // Día libre de la rutina sin práctica: rayado (no cortó la racha).
    const free =
      !rec &&
      !isFuture &&
      routineWeekdays !== null &&
      routineSince !== null &&
      dt.getTime() >= routineSince.getTime() &&
      !routineWeekdays.includes(weekdayOf(dt));
    const cls = ['sk-cell', `lv${tier}`, rec ? 'has' : '', isFuture ? 'future' : '', isToday ? 'today' : '', free ? 'rt-free' : '']
      .filter(Boolean)
      .join(' ');
    cells.push(
      <div key={d} className={cls} onClick={rec ? () => onPickDay({ y, m, d, rec }) : undefined}>
        {tier === 5 && (
          <span className="sk-hour-mark">
            <StreakFlame size={10} color="#2a1408" animate={false} />
          </span>
        )}
        <span className="sk-dn">{d}</span>
        {rec && (
          <span className="sk-metrics">
            <span className="sk-mins">{rec.minutes}′</span>
            {rec.vocabUsed > 0 && (
              <span
                className="sk-vocab"
                title={`${rec.vocabUsed} ${rec.vocabUsed === 1 ? 'palabra nueva' : 'palabras nuevas'}`}
              >
                {/* 8px se leía como un "+"; ≥9px la chispa se ve como tal */}
                <IconSpark size={9} />
                {rec.vocabUsed}
              </span>
            )}
          </span>
        )}
      </div>,
    );
  }
  return (
    <>
      <div className="sk-dow-row">
        {DOW.map((d, i) => (
          <div key={i} className="sk-dow">{d}</div>
        ))}
      </div>
      <div className="sk-grid">{cells}</div>
    </>
  );
}

// ─── Detalle de un día ───────────────────────────────────────────────────────

/**
 * Sesiones del día pedido, lazy. `null` mientras carga; `[]` cuando el día no
 * tiene sesiones que desglosar o el fetch falló (el detalle sigue mostrando
 * los totales del día, que ya vienen del calendario — un error de red no
 * puede vaciar la hoja).
 */
function useDayBreakdown(day: SelectedDay, tz: string): DayBreakdown | null {
  const { y, m, d } = day;
  const [breakdown, setBreakdown] = useState<DayBreakdown | null>(null);
  // Etiqueta local del día, tal como la emite `/progress/daily`.
  const label = `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

  useEffect(() => {
    const ac = new AbortController();
    let alive = true;
    setBreakdown(null);
    void listSessionsForDay(label, tz, ac.signal)
      .then((sessions) => {
        if (alive) setBreakdown(buildDayBreakdown(sessions));
      })
      .catch(() => {
        if (alive) setBreakdown({ items: [], cats: [] });
      });
    return () => {
      alive = false;
      ac.abort();
    };
  }, [label, tz]);

  return breakdown;
}

function DayDetail({ day, tz, onBack }: { day: SelectedDay; tz: string; onBack: () => void }) {
  const { y, m, d, rec } = day;
  const dt = new Date(y, m, d);
  const title = `${DOW_FULL[dt.getDay()] ?? ''} ${d} de ${MONTHS[m] ?? ''}`;
  const breakdown = useDayBreakdown(day, tz);
  const cats = breakdown?.cats ?? [];
  const totalCat = cats.reduce((a, c) => a + c.minutes, 0) || 1;
  const isHour = rec.minutes >= 60;
  return (
    <>
      <div className="sk-detail-head">
        <button type="button" className="sk-back" onClick={onBack} aria-label="Volver">
          <IconChevronLeft />
        </button>
        <div className="sk-detail-date">{title}</div>
      </div>

      <div className="sk-detail-totals">
        <div className={`sk-total-card${isHour ? ' hot' : ''}`}>
          <div className="k">Tiempo</div>
          <div className="v">{rec.minutes}<span className="u">min</span></div>
          {isHour && (
            <span className="sk-hour-badge">
              <StreakFlame size={11} animate={false} /> 1 hora o más
            </span>
          )}
        </div>
      </div>

      <div className="sk-wordstats">
        <div className="sk-wordstat">
          <span className="ic" style={{ background: 'rgba(212,242,62,0.14)', color: 'var(--lime)' }}>
            <IconPlus />
          </span>
          <div>
            <div className="n">{rec.vocabUsed}</div>
            <div className="l">vocabulario usado</div>
          </div>
        </div>
        <div className="sk-wordstat">
          <span className="ic" style={{ background: 'rgba(255,157,108,0.16)', color: 'var(--coral)' }}>
            <IconCheck />
          </span>
          <div>
            <div className="n">{rec.corrections}</div>
            <div className="l">correcciones</div>
          </div>
        </div>
      </div>

      {cats.length > 0 && (
        <>
          <div className="streak-cal-label" style={{ margin: '0 2px 9px' }}>Minutos por categoría</div>
          <div className="sk-catbar">
            {cats.map((c, i) => (
              <span key={i} style={{ flex: c.minutes / totalCat, background: c.color }} />
            ))}
          </div>
          <div className="sk-catlegend">
            {cats.map((c, i) => (
              <span key={i} className="sk-catleg">
                <span className="dot" style={{ background: c.color }} />
                {c.label} <b>{c.minutes}′</b>
              </span>
            ))}
          </div>
        </>
      )}

      <div className="streak-cal-label" style={{ margin: '18px 2px 4px' }}>Lo que hiciste</div>
      <div>
        {breakdown === null ? (
          <div className="sk-item-meta" style={{ padding: '4px 2px' }}>Cargando…</div>
        ) : (
          breakdown.items.map((it, i) => {
            const color = cats.find((c) => c.type === it.type)?.color ?? 'var(--text-muted)';
            return (
              <div key={i} className="sk-item">
                <span className="sk-item-ic" style={{ background: `${color}1f`, color }}>
                  {typeIcon(it.type)}
                </span>
                <div className="sk-item-info">
                  <div className="sk-item-label">{it.label}</div>
                  <div className="sk-item-meta">{it.minutes} min</div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </>
  );
}

// ─── Hoja ────────────────────────────────────────────────────────────────────

interface StreakSheetProps {
  /** Calendario ya armado desde `/progress/daily` (ver `useHomeStats`). */
  calendar: StreakCalendar;
  now: Date;
  /** Racha actual — del backend (/progress/totals), no se computa acá. */
  current: number;
  /** Mejor marca histórica — ídem backend. */
  longest: number;
  todayDone: boolean;
  minutesToday: number;
  onClose: () => void;
  onPractice: () => void;
  variant?: 'sheet' | 'modal';
  /**
   * «Tu rutina» (card #547). Con rutina la racha cuenta sus días (el backend ya
   * saltea los libres), la hoja muestra la rutina con «Cambiar», los días
   * libres rayados y la meta del día. `null` = sin rutina: como siempre.
   */
  routine?: Routine | null;
  onEditRoutine?: () => void;
}

export function StreakSheet({
  calendar,
  now,
  current,
  longest,
  todayDone,
  minutesToday,
  onClose,
  onPractice,
  variant = 'sheet',
  routine = null,
  onEditRoutine,
}: StreakSheetProps) {
  const { grabProps, sheetStyle } = useSheetDrag(onClose);
  const [cursor, setCursor] = useState({ y: now.getFullYear(), m: now.getMonth() });
  const [selected, setSelected] = useState<SelectedDay | null>(null);

  const start = calendar.startDate ?? new Date(now.getFullYear(), now.getMonth(), 1);
  const minMonth = new Date(start.getFullYear(), start.getMonth(), 1);
  const maxMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const isStartMonth = cursor.y === minMonth.getFullYear() && cursor.m === minMonth.getMonth();
  const isCurMonth = cursor.y === maxMonth.getFullYear() && cursor.m === maxMonth.getMonth();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  const step = (delta: number) => {
    setCursor((c) => {
      const nd = new Date(c.y, c.m + delta, 1);
      const clamped = nd < minMonth ? minMonth : nd > maxMonth ? maxMonth : nd;
      return { y: clamped.getFullYear(), m: clamped.getMonth() };
    });
  };

  return (
    <div
      className={`streak-overlay${variant === 'modal' ? ' is-modal' : ''}`}
      onClick={onClose}
      role="presentation"
    >
      <div
        className="streak-sheet"
        style={sheetStyle}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="bal-handle sheet-grab" {...grabProps} />
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <span className="script" style={{ fontSize: 26, lineHeight: 1, color: 'var(--text)' }}>Tu racha</span>
            <ShortUnderline color="var(--streak)" width={96} style={{ marginTop: 2 }} />
          </div>
          <button type="button" className="streak-close" onClick={onClose} aria-label="Cerrar">
            <IconClose />
          </button>
        </div>

        {/* `calendar.tz` sólo es null sin datos, y entonces no hay día
            clickeable — el guard es para el tipo, no un caso alcanzable. */}
        {selected && calendar.tz ? (
          <DayDetail day={selected} tz={calendar.tz} onBack={() => setSelected(null)} />
        ) : (
          <>
            <div className="streak-hero">
              <div className="streak-bigflame"><StreakFlame size={34} /></div>
              <div className="streak-hero-info">
                <div className="streak-hero-num">
                  <b>{current}</b>
                  <span>
                    {routine
                      ? current === 1
                        ? 'día de tu rutina'
                        : 'días de tu rutina'
                      : current === 1
                        ? 'día seguido'
                        : 'días seguidos'}
                  </span>
                </div>
                <div className="streak-hero-longest">Tu mejor marca: <b>{longest} {longest === 1 ? 'día' : 'días'}</b></div>
              </div>
            </div>

            {routine && (
              <div className="rt-sheet-line">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
                  <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
                </svg>
                <span>
                  <b>{formatRoutine(routine)}</b>
                </span>
                {onEditRoutine && (
                  <button
                    type="button"
                    className="rt-sheet-edit"
                    onClick={() => {
                      onClose();
                      onEditRoutine();
                    }}
                  >
                    Cambiar
                  </button>
                )}
              </div>
            )}

            {todayDone ? (
              <div className="streak-status done">
                <span className="streak-status-ic"><IconCheck size={16} /></span>
                <span className="streak-status-tx">
                  {routine && isRestDay(routine, now) ? (
                    <>
                      Hoy era libre y sumaste igual: <b>{minutesToday} min</b>.
                    </>
                  ) : routine ? (
                    <>
                      Hoy ya cuenta: <b>{minutesToday} de {routine.minutes} min</b>
                      {minutesToday >= routine.minutes ? ' — meta cumplida.' : '.'}
                    </>
                  ) : (
                    <>
                      Hoy ya cuenta. <b>Practicaste {minutesToday} min</b> — tocá un día para ver el detalle.
                    </>
                  )}
                </span>
              </div>
            ) : routine && isRestDay(routine, now) ? (
              <div className="streak-status done">
                <span className="streak-status-ic"><IconCheck size={16} /></span>
                <span className="streak-status-tx">Hoy es libre: tu racha no se corta. Si practicás, suma igual.</span>
              </div>
            ) : (
              <div className="streak-status pending">
                <span className="streak-status-ic"><StreakFlame size={16} animate={false} /></span>
                <span className="streak-status-tx">
                  {!routine
                    ? 'Te falta hoy para no cortar la racha.'
                    : current === 0 && longest > 0
                      ? `Tu mejor marca queda: ${longest} ${longest === 1 ? 'día' : 'días'}. Hoy empieza otra.`
                      : `Hoy falta: ${routine.minutes} min. Tenés hasta medianoche.`}
                </span>
                <button
                  type="button"
                  className="streak-status-cta"
                  onClick={() => {
                    onClose();
                    onPractice();
                  }}
                >
                  Practicar
                </button>
              </div>
            )}

            <div className="sk-monthnav">
              <button type="button" className="sk-navbtn" onClick={() => step(-1)} disabled={isStartMonth} aria-label="Mes anterior">
                <IconChevronLeft />
              </button>
              <span className="lbl">{MONTHS[cursor.m]} {cursor.y}</span>
              <button type="button" className="sk-navbtn" onClick={() => step(1)} disabled={isCurMonth} aria-label="Mes siguiente">
                <IconChevronRight />
              </button>
            </div>

            <MonthCalendar
              cursor={cursor}
              byKey={calendar.byKey}
              today={today}
              onPickDay={setSelected}
              routineWeekdays={routine ? routine.weekdays : null}
              routineSince={routine ? startOfDay(new Date(routine.committedAt)) : null}
            />

            <div className="sk-legend">
              <span className="sk-legend-lbl">menos</span>
              <div className="sk-legend-scale">
                <span className="sk-legend-sw" style={{ background: 'rgba(255,255,255,0.05)' }} />
                <span className="sk-legend-sw" style={{ background: 'rgba(255,184,99,0.18)' }} />
                <span className="sk-legend-sw" style={{ background: 'rgba(255,184,99,0.30)' }} />
                <span className="sk-legend-sw" style={{ background: 'rgba(255,184,99,0.46)' }} />
                <span className="sk-legend-sw" style={{ background: 'rgba(255,184,99,0.64)' }} />
              </div>
              <span className="sk-legend-lbl">más</span>
              <span className="sk-legend-hour"><i /> 1h+</span>
            </div>
            {routine && routine.weekdays.length < 7 && (
              <div className="rt-legend">
                <span>
                  <i /> libre: no corta la racha
                </span>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
